import { Box, Theme, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';

import FormSubmitButton from '../../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../../components/buttons/FormCancelButton';
import FormTextField from '../../../components/formFields/FormTextField';
import {
  createRemittanceFormSchema,
  RemittanceTemplateColumnValidation,
} from '../../../helpers/formSchemas/formSchemas';

export interface AddRemittanceRecordColumn
  extends Omit<RemittanceTemplateColumnValidation, 'maxLength'> {
  id: number;
  displayOrder?: number;
  maxLength: number;
  isEditable: boolean;
}

interface AddRemittanceRecordProps {
  columns: AddRemittanceRecordColumn[];
  defaultValues?: Record<string, string>;
  title?: string;
  submitButtonText?: string;
  onCancel: () => void;
  onSubmit: (values: Record<string, string>) => void;
  isSubmitting?: boolean;
}

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '24px',
    marginTop: '24px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    boxSizing: 'border-box',
  },

  title: {
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 600,
    color: theme.palette.common.white,
    marginBottom: '24px',
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },

 fields: {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  width: '100%',
},

  field: {
    width: '100%',
    minWidth: 0,
  },

  actions: {
    display: 'flex',
    gap: '16px',
    marginTop: '24px',
    maxWidth: '416px',
  },
}));

const AddRemittanceRecord = ({
  columns,
  defaultValues,
  title = 'Add Remittance Line',
  submitButtonText = 'Confirm',
  onCancel,
  onSubmit,
  isSubmitting = false,
}: AddRemittanceRecordProps) => {
  const { classes } = useStyles();

  const formMethods = useForm<Record<string, string>>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(createRemittanceFormSchema(columns)),
    defaultValues: defaultValues ?? {},
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = formMethods;

  const formValues = watch();

  const hasAnyValue = columns
    .filter((column) => column.isEditable)
    .some((column) => {
      const value = formValues[column.key];
      return String(value ?? '').trim() !== '';
    });

  useEffect(() => {
    reset(defaultValues ?? {});
  }, [defaultValues, reset]);

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>
        {title}
      </Typography>

      <FormProvider {...formMethods}>
        <Box
          component="form"
          className={classes.form}
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box className={classes.fields}>
            {columns
              .filter((column) => column.isEditable)
              .sort(
                (a, b) =>
                  (a.displayOrder ?? 0) -
                  (b.displayOrder ?? 0),
              )
              .map((column) => (
                <Box key={column.id} className={classes.field}>
                  <FormTextField
                    name={column.key}
                    label={`${column.label}${column.isRequired ? ' *' : ''}`}
                    valueDefinition={
                      column.binding === 'LINE_AMOUNT'
                        ? '$'
                        : undefined
                    }
                    isDescription={column.maxLength > 80}
                    isMultiline={column.maxLength > 80}
                    disabled={
                      isSubmitting || !column.isEditable
                    }
                  />
                </Box>
              ))}
          </Box>

          <Box className={classes.actions}>
            <FormSubmitButton
              disabled={
                !isValid ||
                !hasAnyValue ||
                isSubmitting
              }
              buttonText={submitButtonText}
              onClick={handleSubmit(onSubmit)}
            />

            <FormCancelButton onClick={onCancel} />
          </Box>
        </Box>
      </FormProvider>
    </Box>
  );
};

export default AddRemittanceRecord;